/**
 * Learn DIGU (D-106): eight short lessons. The first three are played on a scripted table (no server); the fourth is
 * illustrated scoring; the last four are screenshots of the app with numbered notes. Progress is kept on this device.
 */
import { useEffect, useState } from "react";
import { cardId, cardValue, rank, suit, type Card } from "@digu/shared";

export type LessonId = "turn" | "groups" | "digu" | "scoring" | "table" | "lobby" | "where" | "shop";
export interface LessonMeta { id: LessonId; title: string; sub: string; icon: string; kind: "play" | "read" }
export const LESSONS: readonly LessonMeta[] = [
  { id: "turn", title: "Your turn", sub: "Draw, then throw one away", icon: "rectangle-vertical", kind: "play" },
  { id: "groups", title: "Groups", sub: "Sets, runs, and why A-2-3 isn't one", icon: "layers", kind: "play" },
  { id: "digu", title: "DIGU", sub: "3 + 3 + 4 and press DIGU", icon: "star", kind: "play" },
  { id: "scoring", title: "Scoring", sub: "+100, loose cards, minus 100", icon: "chart-no-axes-column", kind: "read" },
  { id: "table", title: "The table", sub: "Every button around your hand", icon: "target", kind: "read" },
  { id: "lobby", title: "Before the deal", sub: "Ready, rules, invites", icon: "users", kind: "read" },
  { id: "where", title: "Where to play", sub: "Practice to tournaments", icon: "house", kind: "read" },
  { id: "shop", title: "Boli, tokens and the Shop", sub: "What costs what", icon: "coins", kind: "read" },
];
export const isLessonId = (v: string | undefined): v is LessonId => LESSONS.some((l) => l.id === v);
export const lessonIndex = (id: LessonId): number => LESSONS.findIndex((l) => l.id === id);
export const nextLesson = (id: LessonId): LessonMeta | null => LESSONS[lessonIndex(id) + 1] ?? null;

// ---- Progress (this device) ----
const DONE_KEY = "digu.learn.done";
const OFFER_KEY = "digu.learn.offer";
const EVENT = "digu-learn";

export function readDone(): LessonId[] {
  try { const v = JSON.parse(localStorage.getItem(DONE_KEY) ?? "[]") as unknown; return Array.isArray(v) ? v.filter((x): x is LessonId => typeof x === "string" && isLessonId(x)) : []; } catch { return []; }
}
export function markDone(id: LessonId): void {
  const done = new Set(readDone()); done.add(id);
  try { localStorage.setItem(DONE_KEY, JSON.stringify(LESSONS.map((l) => l.id).filter((x) => done.has(x)))); } catch { /* private window */ }
  window.dispatchEvent(new Event(EVENT));
}
export function useLessonsDone(): LessonId[] {
  const [done, setDone] = useState<LessonId[]>(readDone);
  useEffect(() => {
    const on = () => setDone(readDone());
    window.addEventListener(EVENT, on); window.addEventListener("storage", on);
    return () => { window.removeEventListener(EVENT, on); window.removeEventListener("storage", on); };
  }, []);
  return done;
}
export const progressLine = (done: number): string => (done === 0 ? "Eight short lessons" : done >= LESSONS.length ? "All 8 lessons done" : `${done} of ${LESSONS.length} lessons done`);

/** The "New to DIGU?" offer shows once for a player with no games until they open Learn or dismiss it. */
export function offerLearn(gamesPlayed: number): boolean {
  if (gamesPlayed > 0) return false;
  try { return localStorage.getItem(OFFER_KEY) !== "closed" && readDone().length === 0; } catch { return false; }
}
export function closeLearnOffer(): void { try { localStorage.setItem(OFFER_KEY, "closed"); } catch { /* private window */ } window.dispatchEvent(new Event(EVENT)); }

// ---- Groups, as the rules define them (docs/RULES.md) ----
/** Three or four cards of one rank, every suit different. */
export function isSet(cards: readonly Card[]): boolean {
  if (cards.length < 3 || cards.length > 4) return false;
  return cards.every((c) => rank(c) === rank(cards[0]!)) && new Set(cards.map(suit)).size === cards.length;
}
/** Three or four in a row in one suit; aces never join a run. */
export function isRun(cards: readonly Card[]): boolean {
  if (cards.length < 3 || cards.length > 4) return false;
  if (cards.some((c) => rank(c) === 1)) return false;
  if (!cards.every((c) => suit(c) === suit(cards[0]!))) return false;
  const r = cards.map(rank).sort((a, b) => a - b);
  return r.every((x, i) => i === 0 || x === r[i - 1]! + 1);
}
export const isGroup = (cards: readonly Card[]): boolean => isSet(cards) || isRun(cards);
/** Why a pick isn't a group, in one sentence for the coach card. */
export function groupVerdict(cards: readonly Card[]): string {
  if (cards.length < 3) return "A group needs three or four cards.";
  if (cards.length > 4) return "A group has at most four cards.";
  if (isSet(cards)) return "That's a set: one rank, different suits.";
  if (isRun(cards)) return "That's a run: in a row, one suit.";
  const oneSuit = cards.every((c) => suit(c) === suit(cards[0]!));
  if (oneSuit && cards.some((c) => rank(c) === 1)) return "Not a run: aces never join a run in DIGU.";
  if (oneSuit) return "Not a run: the cards must follow each other with no gap.";
  if (cards.every((c) => rank(c) === rank(cards[0]!))) return "Not a set: each card needs a different suit.";
  return "Not a group: a set is one rank, a run is one suit in a row.";
}

/**
 * The winning split of eleven cards: the one card to throw away and the three groups (3 + 3 + 4) the other ten make,
 * or null. Small search, fine for a lesson hand.
 */
export function winningSplit(hand: readonly Card[]): { discard: Card; groups: Card[][] } | null {
  for (const discard of hand) {
    const rest = hand.filter((c) => c !== discard);
    const g = splitInto(rest, [4, 3, 3]);
    if (g) return { discard, groups: g };
  }
  return null;
}
function splitInto(cards: readonly Card[], sizes: number[]): Card[][] | null {
  if (sizes.length === 0) return cards.length === 0 ? [] : null;
  const [size, ...more] = sizes as [number, ...number[]];
  for (const combo of combos(cards, size)) {
    if (!isGroup(combo)) continue;
    const rest = splitInto(cards.filter((c) => !combo.includes(c)), more);
    if (rest) return [combo, ...rest];
  }
  return null;
}
function combos(cards: readonly Card[], k: number, start = 0): Card[][] {
  if (k === 0) return [[]];
  const out: Card[][] = [];
  for (let i = start; i <= cards.length - k; i++) for (const rest of combos(cards, k - 1, i + 1)) out.push([cards[i]!, ...rest]);
  return out;
}
export const sumValues = (cards: readonly Card[]): number => cards.reduce((s, c) => s + cardValue(c), 0);
/** Lesson-hand order: groups first, each sorted, the spare last. */
export function orderByGroups(hand: readonly Card[], groups: readonly Card[][]): Card[] {
  const sorted = groups.map((g) => [...g].sort((a, b) => rank(a) - rank(b) || suit(a) - suit(b)));
  const inGroups = new Set(sorted.flat());
  return [...sorted.flat(), ...hand.filter((c) => !inGroups.has(c))];
}

// ---- The scripted hands ----
const C = (s: "C" | "D" | "H" | "S", r: number): Card => cardId(s, r);
/** Lesson 1: ten mixed cards, Turtle's 8♦ on the pile, a 5♣ under the stock. */
export const TURN_HAND: readonly Card[] = [C("C", 11), C("H", 12), C("C", 13), C("S", 8), C("H", 9), C("D", 6), C("C", 1), C("S", 3), C("S", 6), C("C", 7)];
export const TURN_PILE: Card = C("D", 8);
export const TURN_STOCK: Card = C("C", 5);
/** Lesson 2: three sevens, a hearts run, the A-2-3 of spades and a stray. */
export const GROUPS_HAND: readonly Card[] = [C("S", 1), C("H", 7), C("H", 4), C("S", 2), C("C", 7), C("H", 5), C("D", 11), C("S", 7), C("H", 6), C("S", 3)];
export const GROUPS_TARGETS = { set: [C("S", 7), C("H", 7), C("C", 7)], run: [C("H", 4), C("H", 5), C("H", 6)], aces: [C("S", 1), C("S", 2), C("S", 3)] } as const;
/** Lesson 3: eleven cards after drawing the 10♦: 9-10-J-Q of diamonds, three kings, 4-5-6 of clubs and a stray 2♥. */
export const DIGU_HAND: readonly Card[] = [C("C", 5), C("D", 9), C("S", 13), C("H", 2), C("D", 11), C("C", 4), C("H", 13), C("D", 12), C("C", 13), C("C", 6), C("D", 10)];

/** Lesson steps. Each names what the coach says and which action moves it on ("next" is the coach's own button). */
export type TurnStep = "intro" | "draw" | "pick" | "throw" | "done";
export type GroupsStep = "intro" | "set" | "run" | "aces" | "done";
export type DiguStep = "intro" | "sort" | "pick" | "declare" | "done";
export const STEPS = {
  turn: ["intro", "draw", "pick", "throw", "done"] as TurnStep[],
  groups: ["intro", "set", "run", "aces", "done"] as GroupsStep[],
  digu: ["intro", "sort", "pick", "declare", "done"] as DiguStep[],
};

/** Lesson 2: does the picked set of cards complete the step? (Order of picking doesn't matter.) */
export function groupsStepDone(step: GroupsStep, picked: readonly Card[]): boolean {
  const same = (t: readonly Card[]) => picked.length === t.length && t.every((c) => picked.includes(c));
  if (step === "set") return isSet(picked);
  if (step === "run") return isRun(picked);
  if (step === "aces") return same(GROUPS_TARGETS.aces);
  return false;
}

// ---- D-215: where Learn was opened from ----
// Learn opens from Welcome, Help, Support, the settings menu, Home's offer and a table's How to play. Closing it goes
// back there, not to a fixed page. Kept per tab (sessionStorage, with memory as a fallback for blocked storage).
const FROM_KEY = "digu.learn.from";
let fromMemory: string | null = null;
const inLearn = (path: string) => path === "/learn" || path.startsWith("/learn/");

/** Called on every route change (App.tsx): entering Learn from elsewhere remembers where from. */
export function noteLearnRoute(prev: string | null, next: string): void {
  if (!inLearn(next) || (prev !== null && inLearn(prev.split("?")[0]!))) return;
  // Opened straight from an address (no page before it): nothing to return to.
  fromMemory = prev;
  try { if (prev) sessionStorage.setItem(FROM_KEY, prev); else sessionStorage.removeItem(FROM_KEY); } catch { /* private window */ }
}

/** Where closing Learn goes: the page it was opened from, else Home (or the front door without an account). */
export function learnExit(fallback: string): string {
  let from = fromMemory;
  try { from = sessionStorage.getItem(FROM_KEY) ?? from; } catch { /* private window */ }
  return from && !inLearn(from.split("?")[0]!) ? from : fallback;
}
